import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CalendarIcon, Plus, ClockArrowUp, Sparkles } from "lucide-react";
import {
  Button,
  Modal,
  Table,
  Row,
  Col,
  Typography,
  Card,
  Progress,
} from "antd";
import {
  listPortalEvents,
  publishPortalEvent,
  listDraftPortalEvents,
  deleteDraftPortalEvent,
  getPortalEventDetail,
} from "services/portalEvents.service";
import { getServiceAccount } from "services/serviceAccount.service";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { useCommunitySelector } from "hooks/useCommunitySelector/useCommunitySelector";
import {
  rowFromPortalList,
  getEventListColumns,
  listingFilterToShowInListings,
  LISTING_FILTER_OPTIONS,
  SHOW_IN_LISTINGS_COPY,
} from "../commons/events.helpers";
import EventsFilterBar from "./_blocks/EventsFilterBar";
import PosterUploadModal from "./_blocks/PosterUploadModal";
import useAuthStore from "stores/AuthStore/AuthStore";

const { Title, Text } = Typography;

function EventsList() {
  const { isRootUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posterModalOpen, setPosterModalOpen] = useState(false);
  const [inProgressDrafts, setInProgressDrafts] = useState([]);
  // Whether this vendor may waive the platform fee: true / false, or null
  // while loading or when the service account can't be read.
  const [canWaivePlatformFee, setCanWaivePlatformFee] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [timeFilter, setTimeFilter] = useState(
    searchParams.get("time") ?? "ALL"
  );
  // "Show in listings" filter: "shown" | "link_only" | null (all). Server-side.
  const [listingFilter, setListingFilter] = useState(() => {
    const listing = searchParams.get("listing");
    return LISTING_FILTER_OPTIONS.some((o) => o.value === listing)
      ? listing
      : null;
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const {
    loading: loadingCommunity,
    communityOptions,
    selectedCommunityId: communityFilter,
    setSelectedCommunityId: setCommunityFilter,
  } = useCommunitySelector({
    isRootUser,
    initialCommunityId: searchParams.get("community_id") ?? null,
  });

  const mergeParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (!v) next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  };

  async function loadEvents(
    page,
    pageSize,
    community,
    time,
    publicationStatus,
    listing
  ) {
    setLoading(true);
    try {
      const showInListings = listingFilterToShowInListings(listing);
      const body = {
        current_page: page,
        page_size: pageSize,
        ...(publicationStatus && { publication_status: publicationStatus }),
        ...(community && { community_id: [community] }),
        ...(time === "UPCOMING" &&
          time !== "ALL" && { include_expired: false }),
        ...(time === "PAST" && time !== "ALL" && { only_past: true }),
        ...(showInListings !== undefined && {
          show_in_listings: showInListings,
        }),
      };
      const { data: envelope, errors } = await listPortalEvents(body);
      if (errors?.length) {
        renderErrorNotifications(errors);
        setEvents([]);
        return;
      }
      const rows = Array.isArray(envelope?.data) ? envelope.data : [];
      setEvents(rows.map(rowFromPortalList));
      const meta = envelope?.meta?.pagination ?? {};
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: meta.total ?? 0,
      }));
    } catch {
      renderErrorNotifications([{ message: "Failed to load events." }]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEvents(1, 10, communityFilter, timeFilter, undefined, listingFilter);
    listDraftPortalEvents().then(({ data }) => {
      if (Array.isArray(data)) setInProgressDrafts(data);
    });
    getServiceAccount().then(({ data, errors }) => {
      if (!errors?.length && data?.vendor) {
        setCanWaivePlatformFee(data.vendor.can_waive_platform_fee === true);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteDraft = (eventId) => {
    Modal.confirm({
      title: "Delete this draft?",
      icon: null,
      content:
        "This will permanently delete the in-progress event and cannot be undone.",
      okText: "Delete",
      okType: "danger",
      maskClosable: true,
      onOk: async () => {
        const { errors } = await deleteDraftPortalEvent(eventId);
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
        if (errors?.length > 0) {
          renderErrorNotifications(errors);
          return;
        }
        setInProgressDrafts((prev) =>
          prev.filter((d) => d.event_id !== eventId)
        );
        renderSuccessNotifications({
          title: "Deleted",
          message: "Draft event deleted.",
        });
      },
    });
  };

  const handleCommunityChange = (val) => {
    const community = val ?? null;
    setCommunityFilter(community);
    mergeParams({ community_id: community });
    loadEvents(
      1,
      pagination.pageSize,
      community,
      timeFilter,
      statusFilter,
      listingFilter
    );
  };

  const handleStatusChange = (val) => {
    const status = val ?? null;
    setStatusFilter(status);
    mergeParams({ status });
    loadEvents(
      1,
      pagination.pageSize,
      communityFilter,
      timeFilter,
      status,
      listingFilter
    );
  };

  const handleTimeChange = (val) => {
    const time = val ?? "ALL";
    setTimeFilter(time);
    mergeParams({ time: time === "ALL" ? null : time });
    loadEvents(
      1,
      pagination.pageSize,
      communityFilter,
      time,
      statusFilter,
      listingFilter
    );
  };

  const handleListingChange = (val) => {
    const listing = val ?? null;
    setListingFilter(listing);
    mergeParams({ listing });
    loadEvents(
      1,
      pagination.pageSize,
      communityFilter,
      timeFilter,
      statusFilter,
      listing
    );
  };

  const handleTableChange = (pag) => {
    loadEvents(
      pag.current,
      pag.pageSize,
      communityFilter,
      timeFilter,
      statusFilter,
      listingFilter
    );
  };

  const filtered = events.filter((e) => {
    const q = search.toLowerCase();
    return !q || e.name.toLowerCase().includes(q);
  });

  const handlePublish = (event) => {
    Modal.confirm({
      title: "Publish this event?",
      icon: null,
      content: (
        <div style={{ paddingTop: 4 }}>
          <p>
            You are about to publish <strong>{event.name}</strong>.
          </p>
          {event.showInListings === false && (
            <p className="mt-2">
              {SHOW_IN_LISTINGS_COPY.publishConfirmLinkOnly}
            </p>
          )}
          <p className="text-red-500 font-medium mt-2">
            ⚠️ This action is irreversible. Once published, any changes related
            to tickets,media,venue,prices,etc cannot be edited.
          </p>
          {!event.chargePlatformFee &&
            (canWaivePlatformFee === false ? (
              <p className="text-amber-500 font-medium mt-2">
                Your account can no longer waive the platform fee, so it will
                be switched back on when you publish. Attendees will pay it.
              </p>
            ) : (
              <p className="text-amber-500 font-medium mt-2">
                Attendees won&apos;t pay the platform fee. It will be deducted
                from your earnings. This can&apos;t be changed after publishing.
              </p>
            ))}
        </div>
      ),
      maskClosable: true,
      okText: "Publish Now",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const { errors } = await publishPortalEvent(event.id);
        if (errors?.length) {
          renderErrorNotifications(errors);
          return;
        }
        // Core switches a waived event's fee back on at first publish when the
        // vendor can no longer waive it; read the result so the organiser is told.
        let chargePlatformFee = event.chargePlatformFee;
        if (!event.chargePlatformFee) {
          const { data: detail } = await getPortalEventDetail(event.id);
          if (detail) chargePlatformFee = detail.charge_platform_fee !== false;
        }
        const feeSwitchedOn = !event.chargePlatformFee && chargePlatformFee;
        renderSuccessNotifications({
          title: "Event Published!",
          message: feeSwitchedOn
            ? `"${event.name}" is now live. The platform fee was switched back on because your account can no longer waive it.`
            : `"${event.name}" is now live.`,
        });
        setEvents((prev) =>
          prev.map((e) =>
            e.id === event.id
              ? { ...e, status: "PUBLISHED", chargePlatformFee }
              : e
          )
        );
      },
    });
  };

  const handleClone = (eventId) => {
    navigate(`/shoup-community/events/clone/${eventId}`);
  };

  const tableColumns = getEventListColumns({ handlePublish, handleClone });
  const hasActiveFilters =
    search ||
    statusFilter !== null ||
    communityFilter !== null ||
    timeFilter !== null ||
    listingFilter !== null;
  // Link-only empty state: Listing = Link only and no other filter narrowing it.
  const showLinkOnlyEmpty =
    listingFilter === "link_only" &&
    !search &&
    statusFilter === null &&
    communityFilter === null &&
    (!timeFilter || timeFilter === "ALL");

  return (
    <div className="space-y-6">
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Events
          </Title>
          <Text type="secondary">
            Manage and track all your community events.
          </Text>
        </Col>
        <Col>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={<Sparkles size={14} />}
              onClick={() => setPosterModalOpen(true)}
            >
              Create from poster
            </Button>
            <Button type="primary" icon={<Plus size={14} />}>
              <Link to="/shoup-community/events/create">Create Event</Link>
            </Button>
          </div>
        </Col>
      </Row>

      {inProgressDrafts.length > 0 && (
        <Card
          size="small"
          title={
            <span className="flex items-center gap-2">
              <ClockArrowUp className="h-4 w-4" />
              {inProgressDrafts.length} events in progress
            </span>
          }
          style={{ marginBottom: 0 }}
        >
          <Row gutter={[12, 12]}>
            {inProgressDrafts.map((draft) => (
              <Col key={draft.event_id} xs={24} sm={8}>
                <div
                  className="flex items-center justify-between rounded-lg border px-3 py-2 bg-card"
                  style={{ height: "100%" }}
                >
                  <div style={{ flex: 1, marginRight: 12, minWidth: 0 }}>
                    <p
                      className="font-medium text-sm"
                      style={{
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {draft.title || "Untitled Event"}
                    </p>
                    <Progress
                      percent={Math.round((draft.current_step / 5) * 100)}
                      size="small"
                      format={() => `${draft.current_step}/5`}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/shoup-community/events/${draft.event_id}/edit`
                        )
                      }
                    >
                      Resume
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteDraft(draft.event_id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <EventsFilterBar
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusChange={handleStatusChange}
        timeFilter={timeFilter}
        onTimeChange={handleTimeChange}
        listingFilter={listingFilter}
        onListingChange={handleListingChange}
        communityFilter={communityFilter}
        onCommunityChange={handleCommunityChange}
        communityOptions={communityOptions}
        loadingCommunity={loadingCommunity}
      />

      {!loading && filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-4 bg-card rounded-2xl border border-dashed">
          <CalendarIcon className="h-10 w-10 text-foreground/50" />
          <div>
            <Title level={4}>
              {showLinkOnlyEmpty
                ? SHOW_IN_LISTINGS_COPY.emptyLinkOnlyTitle
                : "No events found"}
            </Title>
            <Text type="secondary">
              {showLinkOnlyEmpty
                ? SHOW_IN_LISTINGS_COPY.emptyLinkOnlyBody
                : hasActiveFilters
                  ? "Try adjusting your filters."
                  : "Create your first event to start accepting registrations."}
            </Text>
          </div>
          {!hasActiveFilters && (
            <Button icon={<Plus size={14} />}>
              <Link to="/shoup-community/events/create">
                Create Your First Event
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <Table
          rowKey="id"
          columns={tableColumns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 700 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            placement: "bottomRight",
          }}
          onChange={handleTableChange}
        />
      )}

      {posterModalOpen && (
        <PosterUploadModal
          open={posterModalOpen}
          onClose={() => setPosterModalOpen(false)}
        />
      )}
    </div>
  );
}

export default EventsList;
